import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO, { softwareAppSchema, faqSchema } from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function VsDuolingo() {
  const [openFaq, setOpenFaq] = useState(null);
  
  const faqs = [
    { question: "What is the Duolingo English Test?", answer: "The Duolingo English Test (DET) is a modern English proficiency assessment for international students and institutions. It's an online, adaptive test." },
    { question: "How does CheckScript help with English proficiency preparation?", answer: "While taking mock tests, CheckScript AI can instantly translate complex vocabulary, explain grammatical nuances, and capture specific question formats for later review, accelerating your learning." },
    { question: "Can CheckScript capture audio?", answer: "CheckScript's primary function is screen capture and visual AI analysis. We are continually expanding our features." },
    { question: "Does CheckScript support multiple languages?", answer: "Yes, our AI backend supports processing and explaining content in over 15 languages." },
    { question: "Can I use CheckScript entirely offline?", answer: "Yes, by utilizing the Neural Driver feature with a local AI model, you can get study assistance without an active internet connection." }
  ];

  return (
    <SEOPageLayout>
      <SEO
        title="Duolingo English Test Preparation — AI Study Companion | CheckScript"
        description="Accelerate your language learning and test preparation. CheckScript AI provides instant explanations and study material capture for language proficiency tests."
        keywords="Duolingo test helper, Duolingo English Test preparation, DET alternative, Duolingo exam AI, Duolingo test companion"
        path="/alternatives/duolingo"
        structuredData={{
          ...softwareAppSchema(),
          ...faqSchema(faqs)
        }}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">🌐 Language Test Prep</div>
          <h1 className="seo-h1">
            Master Language Exams with <span className="seo-gradient-text">AI Assistance</span>
          </h1>
          <p className="seo-lead">
            Preparing for proficiency exams like the Duolingo English Test requires constant practice. CheckScript AI serves as your personal tutor, providing instant explanations for complex grammar and vocabulary during mock tests.
          </p>
          <div className="seo-hero-actions">
            <Link to="/download" className="seo-btn seo-btn--primary">Enhance Your Prep</Link>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container">
          <div className="seo-bento-grid" style={{ alignItems: 'center', gap: '48px' }}>
            <div>
              <h2 className="seo-h2">The Challenge of Language Exams</h2>
              <p className="seo-text-secondary seo-mb-24">
                Adaptive language tests continuously adjust their difficulty. When practicing with mock tests, identifying exactly why an answer is wrong—whether due to subtle grammatical rules or uncommon vocabulary—is crucial for improvement. Standard practice platforms often lack deep, contextual explanations.
              </p>
              <h3 className="seo-h3 seo-mt-32">The CheckScript Approach</h3>
              <p className="seo-text-secondary seo-mb-24">
                With a single hotkey, CheckScript AI analyzes the question on your screen. It doesn't just give the answer; it breaks down the sentence structure, defines difficult words, and explains the underlying language rules, turning every practice question into a mini-lesson.
              </p>
            </div>
            <div className="seo-glass-card">
              <h3 className="seo-h3 seo-mb-24">Study Benefits</h3>
              <ul className="seo-feature-list">
                <li>Instant grammar and vocabulary breakdowns</li>
                <li>Archive difficult questions for review</li>
                <li>Works with any online mock test platform</li>
                <li>Multi-language explanation support</li>
                <li>Offline study capability (Neural Driver)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-section seo-animate">
        <div className="seo-container">
          <h2 className="seo-h2 seo-text-center seo-mb-48">How CheckScript Accelerates Learning</h2>
          <div className="seo-table-wrap">
            <table className="seo-table">
              <thead>
                <tr>
                  <th>Learning Aspect</th>
                  <th>Standard Practice</th>
                  <th className="seo-highlight-col">With CheckScript AI</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Error Analysis</td>
                  <td>Basic right/wrong feedback</td>
                  <td className="seo-highlight-col">Detailed contextual explanations</td>
                </tr>
                <tr>
                  <td>Vocabulary</td>
                  <td>Manual dictionary lookups</td>
                  <td className="seo-highlight-col">Instant in-context definitions</td>
                </tr>
                <tr>
                  <td>Study Material</td>
                  <td>Difficult to save questions</td>
                  <td className="seo-highlight-col">One-click screen capture archiving</td>
                </tr>
                <tr>
                  <td>Pacing</td>
                  <td>Slowed by manual research</td>
                  <td className="seo-highlight-col">Rapid, continuous learning loop</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container seo-container--sm">
          <h2 className="seo-h2 seo-text-center seo-mb-48">Frequently Asked Questions</h2>
          <ul className="seo-faq-list">
            {faqs.map((faq, i) => (
              <li key={i} className={`seo-faq-item ${openFaq === i ? 'active' : ''}`}>
                <button className="seo-faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {faq.question}
                  <span className="seo-faq-arrow">▼</span>
                </button>
                <div className="seo-faq-answer">
                  <div className="seo-faq-answer-inner">{faq.answer}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="seo-cta seo-animate">
        <div className="seo-container">
          <div className="seo-cta-box">
            <h2 className="seo-h2">Achieve Your Target Score</h2>
            <p className="seo-text-secondary seo-mb-32">Download CheckScript and elevate your language exam preparation today.</p>
            <div className="seo-hero-actions">
              <Link to="/download" className="seo-btn seo-btn--primary">Download Free</Link>
            </div>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
